import React from "react";
import { FormikValues } from "formik";
import debounce from "lodash.debounce";

import { DEFAULT_DEBOUNCE } from "@config/consts";
import { URSSAF_FORM, URSSAF_FORM_ERRORS } from "@config/testIds/urssaf";

import { FrenchPostalCode } from "@typings/globals";

import { OptionPanel } from "@ui/Atoms";
import { Row, Column } from "@ui/Atoms/Grid";
import { FormikSelectExtended } from "@ui/Molecules";
import { FormikInputWithError } from "@ui/Molecules/FormikInputWithError";

import { useTranslate } from "@hooks/useTranslate";

import {
  StyledColumn,
  Heading,
  FIELDS,
  UrssafDropdownCustomOption,
  UrssafDropdownCustomSingleValue,
} from "../../UrssafRegistrationForm.helpers";

import {
  laneTypeOptions,
  laneLetterTypeOptions,
  getPostalCodeOptions,
} from "./Address.helpers";

interface Props {
  values: FormikValues;
  postalCodes: FrenchPostalCode[];
  handleChange: (e: React.ChangeEvent<HTMLElement>) => void;
  setFieldValue: (field: FIELDS, value: string | Date | null) => void;
  handleFetchPostalCodes: (input: string) => void;
}

const Address = (props: Props): React.ReactElement => {
  const translate = useTranslate("batmaid_pwa_urssaf");
  const translateGeneric = useTranslate("batmaid_pwa_generic");

  // API is responsible for filtering
  const filterOption = () => true;
  const handleSelectChange = ({
    name,
    value,
  }: {
    name: string;
    value: string;
  }) => {
    props.setFieldValue(name as FIELDS, value);
  };

  return (
    <OptionPanel>
      <Row>
        <Column lg={10} centered>
          <Heading>{translate("subsection_address_and_contact")}</Heading>
          <Row>
            <StyledColumn md={6}>
              <FormikSelectExtended
                name={FIELDS.POSTAL_CODE}
                placeholder={translate("form_postal_code")}
                options={getPostalCodeOptions(props.postalCodes)}
                value={props.values[FIELDS.POSTAL_CODE]}
                onChange={handleSelectChange}
                customSingleValue={UrssafDropdownCustomSingleValue}
                customOption={UrssafDropdownCustomOption}
                onInputChange={debounce(
                  value => props.handleFetchPostalCodes(value),
                  DEFAULT_DEBOUNCE,
                )}
                filterOption={filterOption}
                testId={URSSAF_FORM.POSTAL_CODE}
                errorTestId={URSSAF_FORM_ERRORS.POSTAL_CODE}
              />
            </StyledColumn>
          </Row>

          <Row>
            <StyledColumn md={2} withOptionalLabel>
              <FormikInputWithError
                name={FIELDS.LANE_NUMBER}
                label={translate("form_lane_number")}
                value={props.values[FIELDS.LANE_NUMBER]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.LANE_NUMBER}
                errorTestId={URSSAF_FORM_ERRORS.LANE_NUMBER}
              />
            </StyledColumn>
            <StyledColumn md={2} withOptionalLabel>
              <FormikSelectExtended
                isSearchable={false}
                name={FIELDS.LANE_TYPE}
                placeholder={translate("form_lane_type")}
                options={laneTypeOptions}
                value={props.values[FIELDS.LANE_TYPE]}
                onChange={handleSelectChange}
                testId={URSSAF_FORM.LANE_TYPE}
                errorTestId={URSSAF_FORM_ERRORS.LANE_TYPE}
              />
            </StyledColumn>
            <StyledColumn md={6} withOptionalLabel>
              <FormikInputWithError
                name={FIELDS.ROAD_NAME}
                label={translate("form_road_name")}
                value={props.values[FIELDS.ROAD_NAME]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.STREET_NAME}
                errorTestId={URSSAF_FORM_ERRORS.STREET_NAME}
              />
            </StyledColumn>
            <StyledColumn md={2} withOptionalLabel>
              <FormikSelectExtended
                isSearchable={false}
                name={FIELDS.LANE_LETTER}
                placeholder={translate("form_lane_letter")}
                optionalLabel={translateGeneric("optional")}
                options={laneLetterTypeOptions}
                value={props.values[FIELDS.LANE_LETTER]}
                onChange={handleSelectChange}
                testId={URSSAF_FORM.LANE_LETTER}
              />
            </StyledColumn>
          </Row>

          <Row>
            <StyledColumn withOptionalLabel>
              <FormikInputWithError
                name={FIELDS.ADDITIONAL_ADDRESS}
                label={translate("form_additional_address")}
                optionalLabel={translateGeneric("optional")}
                value={props.values[FIELDS.ADDITIONAL_ADDRESS]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.ADDITIONAL_ADDRESS}
              />
            </StyledColumn>
          </Row>

          <Row>
            <StyledColumn md={6}>
              <FormikInputWithError
                name={FIELDS.MOBILE_PHONE}
                label={translate("form_mobile_phone")}
                value={props.values[FIELDS.MOBILE_PHONE]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.MOBILE_PHONE}
                errorTestId={URSSAF_FORM_ERRORS.MOBILE_PHONE}
              />
            </StyledColumn>
            <StyledColumn md={6}>
              <FormikInputWithError
                name={FIELDS.EMAIL}
                label={translate("form_email")}
                value={props.values[FIELDS.EMAIL]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.EMAIL}
                errorTestId={URSSAF_FORM_ERRORS.EMAIL}
              />
            </StyledColumn>
          </Row>
        </Column>
      </Row>
    </OptionPanel>
  );
};

export { Address };
